/* eslint-disable camelcase */
import { createReducer } from '@reduxjs/toolkit';
import { LoginActions } from './actions';
import Constants from './constants';

const initialState = {
  isSubmitting: false,
  error: null,
  requiresCaptcha: false,
  ssoProviders: null,
  fields: {},
  // 2FA
  requires2fa: false,
  challengeId: null,
  twoFaEmail: null,
  twoFaTriggers: [],
  resendAvailableIn: 0,
  // Bumped on every resend so the panel restarts its countdown even when the
  // backend returns the same resend_available_in (value equality wouldn't).
  resendNonce: 0,
  twoFaAttemptsRemaining: null,
  twoFaError: null,
  twoFaVerifying: false,
  twoFaResending: false,
};

const clear2fa = (state) => {
  state.requires2fa = false;
  state.challengeId = null;
  state.twoFaEmail = null;
  state.twoFaTriggers = [];
  state.resendAvailableIn = 0;
  state.resendNonce = 0;
  state.twoFaAttemptsRemaining = null;
  state.twoFaError = null;
  state.twoFaVerifying = false;
  state.twoFaResending = false;
};

const ACTION_HANDLER = {
  [LoginActions.submitLogin]: (state) => {
    state.isSubmitting = true;
    state.error = null;
  },
  [LoginActions.submitLoginSucceeded]: (state) => {
    state.isSubmitting = false;
  },
  [LoginActions.submitLoginFailed]: (state, action) => {
    state.isSubmitting = false;
    state.error = action.payload?.error || null;
    state.ssoProviders = action.payload?.sso_providers || null;
  },
  [LoginActions.setRequiresCaptcha]: (state, action) => {
    state.requiresCaptcha = action.payload;
  },
  [LoginActions.updateFields]: (state, action) => {
    state.fields = { ...state.fields, ...action.payload };
  },
  [LoginActions.setRequires2fa]: (state, action) => {
    state.requires2fa = true;
    state.challengeId = action.payload?.challenge_id || null;
    state.twoFaEmail = action.payload?.email || null;
    state.twoFaTriggers = action.payload?.triggers || [];
    state.resendAvailableIn = action.payload?.resend_available_in || 0;
    state.twoFaAttemptsRemaining = null;
    state.twoFaError = null;
  },
  [LoginActions.verify2fa]: (state) => {
    state.twoFaVerifying = true;
    state.twoFaError = null;
  },
  [LoginActions.verify2faFailed]: (state, action) => {
    state.twoFaVerifying = false;
    state.twoFaError = action.payload?.error || null;
    state.twoFaAttemptsRemaining = action.payload?.attempts_remaining ?? state.twoFaAttemptsRemaining ?? null;
  },
  [LoginActions.resend2fa]: (state) => {
    state.twoFaResending = true;
  },
  [LoginActions.resend2faSucceeded]: (state, action) => {
    state.twoFaResending = false;
    state.resendAvailableIn = action.payload?.resend_available_in || 0;
    state.resendNonce += 1;
    // A fresh code was sent — clear the stale incorrect-code state (spec §9).
    state.twoFaError = null;
    state.twoFaAttemptsRemaining = null;
  },
  [LoginActions.resend2faFailed]: (state, action) => {
    state.twoFaResending = false;
    state.twoFaError = action.payload?.error || null;
    if (action.payload?.retry_after_seconds) {
      state.resendAvailableIn = action.payload.retry_after_seconds;
    }
  },
  [LoginActions.reset2fa]: (state) => {
    clear2fa(state);
  },
};

export const loginReducer = createReducer(initialState, ACTION_HANDLER);

export default {
  name: Constants.MODULE_NAME,
  reducer: loginReducer,
};
